import { dayjs, parseIsoToTz } from '../lib/dayjs';
import { TIERS } from '../types/TIERS';

/**
 * Left-pads a number to two characters (e.g. 3 -> '03').
 * Pure helper with no timezone implications.
 */
export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Formats a date/ISO string into `YYYY-MM-DD` using Europe/Madrid timezone.
 */
export function fechaLocalYYYYMMDD(d: Date | string): string {
  const dayjsObj = typeof d === 'string' ? parseIsoToTz(d) : dayjs(d);
  return dayjsObj.format('YYYY-MM-DD');
}

/**
 * Returns the next local midnight (Europe/Madrid) after the provided Date.
 */
export function nextMidnight(d: Date): Date {
  const dayjsObj = dayjs(d);
  return dayjsObj.add(1, 'day').startOf('day').toDate();
}

/**
 * Computes duration in hours between two ISO strings in Europe/Madrid.
 * Returns 0 for invalid inputs or non-positive ranges.
 */
export function getDurationHours(start: string, end: string): number {
  try {
    const inicio = parseIsoToTz(start);
    const fin = parseIsoToTz(end);
    if (!inicio.isValid() || !fin.isValid()) {
      return 0;
    }
    const hours = fin.diff(inicio, 'hour', true);
    return hours > 0 ? hours : 0;
  } catch {
    return 0;
  }
}

export function formatDateTime(iso: string): string {
  return formatLocal(iso, { dateOnly: false });
}

/**
 * Formats an ISO string or Date into local strings (Europe/Madrid).
 * - When `dateOnly` is true, returns `YYYY-MM-DD`.
 * - Otherwise returns `YYYY-MM-DD HH:mm`.
 */
export function formatLocal(
  iso: string | Date,
  options: { dateOnly?: boolean } = { dateOnly: false }
): string {
  // Parse as Madrid time for consistent display
  const d = typeof iso === 'string' ? parseIsoToTz(iso) : dayjs(iso);
  if (!d.isValid()) {
    return String(iso);
  }
  if (options.dateOnly) {
    return d.format('YYYY-MM-DD');
  }
  return d.format('YYYY-MM-DD HH:mm');
}

/**
 * Splits an interval [inicio, fin) into day-bounded segments in Europe/Madrid.
 * Returns ISO strings in `YYYY-MM-DDTHH:mm` (no timezone suffix).
 */
export function splitIntervalByDay(
  inicioIso: string,
  finIso: string
): Array<{ inicio: string; fin: string }> {
  try {
    const inicio = parseIsoToTz(inicioIso);
    const fin = parseIsoToTz(finIso);
    if (!inicio.isValid() || !fin.isValid()) {
      return [];
    }
    if (!fin.isAfter(inicio)) {
      return [];
    }

    const segmentos: Array<{ inicio: string; fin: string }> = [];
    let cursor = inicio;
    while (cursor.isBefore(fin)) {
      const limite = cursor.add(1, 'day').startOf('day');
      const finSeg = fin.isBefore(limite) ? fin : limite;
      segmentos.push({
        inicio: cursor.format('YYYY-MM-DDTHH:mm'),
        fin: finSeg.format('YYYY-MM-DDTHH:mm'),
      });
      cursor = finSeg;
    }
    return segmentos;
  } catch {
    return [];
  }
}

/**
 * Maps worked hours to the tier percentage defined in TIERS.
 * Returns 0 when hours exceed configured tiers.
 */
export function getTierPercentageForHours(horas: number): number {
  for (const tier of TIERS) {
    if (horas <= tier.max) {
      return tier.percent;
    }
  }
  return 0;
}

/**
 * Calculates the month bucket key (`YYYY-MM`) for a trip range.
 * Uses Europe/Madrid boundaries and applies the rule: if a trip crosses into the
 * next month but ends within the first five days, it stays in the starting month;
 * otherwise it belongs to the ending month.
 *
 * @throws Error when dates are invalid or fin is not after inicio.
 */
export function getMonthKeyForRange(inicioIso: string, finIso: string): string {
  const inicio = parseIsoToTz(inicioIso);
  const fin = parseIsoToTz(finIso);
  if (!inicio.isValid() || !fin.isValid()) {
    throw new Error('Invalid date range');
  }
  if (!fin.isAfter(inicio)) {
    throw new Error('Fin must be after inicio');
  }

  const inicioY = inicio.year();
  const inicioM = inicio.month();
  const finY = fin.year();
  const finM = fin.month();
  const finD = fin.date();

  if (inicioY === finY && inicioM === finM) {
    return `${inicioY}-${String(inicioM + 1).padStart(2, '0')}`;
  }

  const nextMonth = inicio.add(1, 'month');
  if (finY === nextMonth.year() && finM === nextMonth.month() && finD <= 5) {
    return `${inicioY}-${String(inicioM + 1).padStart(2, '0')}`;
  }

  return `${finY}-${String(finM + 1).padStart(2, '0')}`;
}

// Backward-compatible aliases during migration to English naming.
export const segmentarPorDia = splitIntervalByDay;
export const porcentajePorHoras = getTierPercentageForHours;
